import { useRef, useState } from 'react'
import { api, errorMessage } from '../api/client'
import type { CustomerLedger, CustomerTotals } from '../api/types'
import { BillActions, BillFooter, Letterhead } from '../components/bill'
import { PersonPicker } from '../components/PersonPicker'
import { shop } from '../lib/shop'
import {
  Amt,
  Card,
  ConfirmDelete,
  Dt,
  Empty,
  Field,
  NumberCell,
  Note,
  PageHead,
  Wt,
} from '../components/ui'
import { useApi } from '../hooks/useApi'
import { useCustomerPicker } from '../hooks/usePickers'
import { addDays, billFilename, formatDate, formatMoney, today } from '../lib/format'
import { whatsAppLink } from '../lib/phone'

/**
 * One customer whole ledger, and the bill that gets handed over or sent.
 *
 * Two audiences on one screen, and the split between them is the whole layout:
 *
 * The three figures ABOVE the bill are the shop's. What a man has bought and
 * paid across every year he has dealt with the shop, and what he owes today.
 * The date boxes never touch them - a balance is a running fact about a person,
 * not about a window of time, and if picking September could change what
 * somebody owes then two people reading the same page on the same day would
 * come away with different debts. That guarantee is not a matter of remembering
 * to be careful here: the endpoint returning those totals takes an id and
 * nothing else.
 *
 * The bill BELOW is the customer's, and carries one figure - what he owed on
 * the closing date, which is the question its own heading asks. It does not
 * carry his lifetime history, because a receipt for one fortnight is not where
 * a man finds out what he has spent at this shop since 2024.
 *
 * Everything inside the bill div travels: the PDF, the WhatsApp picture, the
 * print. Everything above it stays on this screen.
 */
export function CustomerLedgerPage() {
  const customers = useCustomerPicker()
  const [customerId, setCustomerId] = useState<string | null>(null)

  /*
   * Opens on today, not on everything.
   *
   * This page used to load a customer's whole history. That is cheap now - the
   * heaviest account in the book is 158 lines - but it is cheap only because the
   * book is four months old. At seventeen sales a day the same account reaches
   * about 480 lines inside a year and nine hundred inside two, and drawing nine
   * hundred rows locks a phone for seconds. The time to bound that is before the
   * shop feels it, not after.
   *
   * Opening on one day is safe to do because the بقایا on the bill is the
   * balance as it stood at the closing date, taken from the server rather than
   * added up off the rows. A customer who comes only to pay has bought nothing
   * today and sees no lines - and still sees the right figure, which is the one
   * he came for. Emptying both date boxes puts the whole history back.
   */
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  /**
   * Whether anybody has asked for the detail yet.
   *
   * <p>Separate from the dates on purpose, because "no dates" has to be able to
   * mean two different things. Before anything is asked for it means nothing has
   * been requested and no bill is drawn. With both date boxes emptied by hand it
   * means the opposite - every entry, unfiltered, the opening balance back on
   * the page and the heading reading «آپ کا کھاتہ».
   */
  const [asked, setAsked] = useState(false)

  /**
   * Picking a span is the asking. Every control that sets dates goes through
   * here.
   *
   * <p>A half-typed span is not a question. Typing the "from" box alone used to
   * fetch and draw the whole stretch from that day to today - a bill nobody
   * asked for, built and thrown away a moment later when the "to" box was
   * filled in. So a hand-typed span waits until both ends are in; the quick
   * buttons pass both at once and run immediately, which is the whole point of
   * them.
   *
   * <p>Two blanks are not a half-span, though. Clearing both boxes is itself the
   * question - every entry, unfiltered - so it goes straight through.
   */
  /*
   * Built once per render rather than inline in the markup, so the buttons and
   * the active test are reading the same three dates and cannot drift apart by
   * a day.
   */
  const spans = [
    { label: 'آج', from: today(), to: today() },
    { label: '7 دن', from: addDays(today(), -7), to: today() },
    { label: '30 دن', from: addDays(today(), -30), to: today() },
  ]

  function ask(newFrom: string, newTo: string) {
    setFrom(newFrom)
    setTo(newTo)

    const bothEnds = Boolean(newFrom) && Boolean(newTo)
    const everything = !newFrom && !newTo

    /*
     * Assigned rather than only ever turned on, so emptying one of the boxes
     * puts the bill away again instead of leaving a half-span one on screen
     * looking finished.
     */
    setAsked(bothEnds || everything)
  }
  const [error, setError] = useState<string | null>(null)
  const billRef = useRef<HTMLDivElement>(null)

  const query = new URLSearchParams()
  if (from) query.set('from', from)
  if (to) query.set('to', to)
  const suffix = query.toString() ? `?${query}` : ''

  /*
   * What a customer owes, the moment he is picked. Three figures, no rows.
   *
   * This is the question the counter asks most - «یہ کتنے دینے ہیں» - and it
   * should not cost two taps. It is also nearly free: a handful of sums on the
   * server and three boxes on the screen. Drawing a man's entries is the
   * expensive part, and that waits below until somebody asks for it.
   */
  const totals = useApi<CustomerTotals>(
    customerId ? `/customers/${customerId}/totals` : null,
  )

  /* The detail, and only once a span has been chosen. */
  const ledger = useApi<CustomerLedger>(
    customerId && asked ? `/customers/${customerId}/ledger${suffix}` : null,
    [from, to, asked],
  )

  const data = ledger.data
  const customer = data?.customer

  /* One day is a date, not a span. "5 Oct تا 5 Oct" is how a form talks. */
  const spanLine = !data?.filtered
    ? 'آپ کا کھاتہ'
    : from === to
      ? `${formatDate(from)} کا حساب`
      : `${formatDate(from || '')} تا ${formatDate(to || '')} کا حساب`

  /**
   * What he owed on the closing date - today's figure when no dates are picked.
   *
   * <p>Read from the server rather than off the last row on the page. Pick a
   * fortnight he happened to buy nothing in and there is no last row, but "what
   * did he owe me then" still has an answer.
   *
   * <p>Negative means the shop owes him, which the label below says in words
   * instead of printing a minus sign at a man who is owed money.
   */
  const owedAtClose = Number(data?.balanceAsAt ?? 0)

  /**
   * One message for both routes - the caption above the shared picture and the
   * text of a direct chat. Written to stand on its own, because the customer may
   * only glance at the message and never open the image.
   */
  const caption = data
    ? `السلام علیکم ${data.customer.name} صاحب\n${spanLine}\nبقایا: ${formatMoney(data.totals.balance)} روپے\nشکریہ — ${shop.name}`
    : ''

  const waLink = customer ? whatsAppLink(customer.phone, caption) : null

  async function repriceSale(id: string, weight: string, rate: string) {
    setError(null)
    try {
      await api.put(`/sales/${id}`, { weight, rate })
      ledger.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  /**
   * A payment has one figure to correct and it lives in the رقم column, so the
   * edit goes straight through rather than via weight and rate. The date and the
   * note are sent back as they were: this is a correction to the amount, not a
   * re-entry of the whole receipt.
   */
  async function correctPayment(id: string, date: string, note: string | undefined, amount: string) {
    setError(null)
    try {
      await api.put(`/payments/${id}`, { date, amount, note: note ?? null })
      ledger.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function removeEntry(kind: 'SALE' | 'PAYMENT', id: string) {
    setError(null)
    try {
      await api.delete(kind === 'SALE' ? `/sales/${id}` : `/payments/${id}`)
      ledger.reload()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <>
      <div className="no-print">
        <PageHead title="کھاتہ" hint="ایک گاہک کا پورا حساب، اور اُس کا بل۔" />

        <Card>
          <div className="row">
            <PersonPicker
              label="گاہک"
              value={customerId}
              /* A different man is a different question. Back to his balance
                 and no bill, rather than showing him the last man's span. */
              onChange={(id) => {
                setCustomerId(id)
                setFrom('')
                setTo('')
                setAsked(false)
              }}
              people={customers.people}
              recentIds={customers.recentIds}
              loading={customers.loading}
            />

            <Field label="اس تاریخ سے">
              <input type="date" value={from} onChange={(e) => ask(e.target.value, to)} />
            </Field>

            <Field label="اس تاریخ تک">
              <input type="date" value={to} onChange={(e) => ask(from, e.target.value)} />
            </Field>
          </div>

          {/*
            The spans anybody actually asks for, one tap each, as a segmented
            control rather than four identical buttons sitting in a row.

            The one being shown is filled dark - the same treatment the tabs
            along the top already use, so the page has one idea of what "this is
            the one you are on" looks like. Four buttons that stay identical
            after a tap do not say which bill is on the screen; whoever picked
            «7 دن» has nothing to confirm that is what they got.
          */}
          {customerId && (
            <div className="span-pick">
              {spans.map((span) => (
                <button
                  key={span.label}
                  type="button"
                  className={`span-pick__btn${
                    from === span.from && to === span.to ? ' is-active' : ''
                  }`}
                  onClick={() => ask(span.from, span.to)}
                >
                  {span.label}
                </button>
              ))}
            </div>
          )}

          {data && (
            <BillActions
              billRef={billRef}
              filename={billFilename(data.customer.name, from, to)}
              caption={caption}
              waLink={waLink}
              enabled
            />
          )}

          {customer && !customer.phone && (
            <p className="muted small">
              اِس گاہک کا نمبر «گاہک» والے صفحے پر ڈال دیں تو سیدھا اُس کی چیٹ کھولنے والا بٹن بھی آ
              جائے گا۔
            </p>
          )}
        </Card>

        {error && <Note kind="error">{error}</Note>}
        {ledger.error && <Note kind="error">{ledger.error}</Note>}
        {!customerId && <Empty>حساب دیکھنے کے لیے اوپر سے گاہک چنیں۔</Empty>}

        {/*
          The lifetime pair, kept out of the bill below on purpose.

          What a man has bought and paid across every year he has dealt with the
          shop is the shop's own figure. It is how Uzair knows whether a name is
          worth the credit it is given, and it is nothing a customer needs
          printed on a receipt for one fortnight. So it lives here, where the
          PDF, the picture and the print never reach.
        */}
        {totals.data && (
          <div className="totals">
            <div className="total-box">
              <div className="total-box__label">کل فروخت (پورے حساب کی)</div>
              <div className="total-box__value">{formatMoney(totals.data.totalSale)}</div>
            </div>
            <div className="total-box">
              <div className="total-box__label">کل ادائیگی (پورے حساب کی)</div>
              <div className="total-box__value">{formatMoney(totals.data.totalPaid)}</div>
            </div>
            <div className={`total-box ${totals.data.settled ? '' : 'total-box--debt'}`}>
              <div className="total-box__label">
                {totals.data.inCredit ? 'آج واپس کرنے ہیں' : 'آج کا بقایا'}
              </div>
              <div className="total-box__value">
                {formatMoney(String(Math.abs(Number(totals.data.balance))))}
              </div>
            </div>
          </div>
        )}

        {/* Picked a man, not yet picked a span. Says which tap brings the detail. */}
        {customerId && !asked && (
          <Empty>
            «آج / 7 دن / 30 دن» میں سے کوئی ایک دبائیں، یا دونوں تاریخیں خود لکھ دیں — تفصیل اور
            بل پھر بنے گا۔ سارا حساب دیکھنا ہو تو دونوں تاریخ کے خانے خالی چھوڑ دیں۔
          </Empty>
        )}

        {/*
          Only when something is actually being held back, and calmly.

          A date is on by default now, so this would otherwise sit on top of
          every customer every time - and a warning that is always there is a
          warning nobody reads. It says plainly what is hidden and which tap
          brings it back, because the bill underneath genuinely is only part of
          the story. The بقایا is not: that is the whole figure, which is why it
          is spelled out here rather than left to be guessed at.
        */}
        {data?.filtered && data.hiddenEntryCount > 0 && (
          <Note kind="info">
            نیچے بل میں صرف <strong>{spanLine.replace(' کا حساب', '')}</strong> کی تفصیل ہے —{' '}
            <span className="amt">{data.hiddenEntryCount}</span> پرانی انٹریاں ابھی نہیں دکھائی
            جا رہیں۔ بقایا پورا ہے ({formatDate(to || '')} تک کا)۔ ساری تفصیل دیکھنی ہو تو دونوں
            تاریخ کے خانے خالی کر دیں۔
          </Note>
        )}
      </div>

      {/* Everything inside this div is what the PDF, the picture and print capture. */}
      {data && (
        <div className="bill" ref={billRef}>
          <Letterhead />

          <div className="bill__body">
            <div className="bill__meta">
              <div>
                <div className="bill__who">{data.customer.name}</div>
                <div className="muted small">{spanLine}</div>
              </div>
              <div className="bill__when">
                <div className="muted small">بل کی تاریخ</div>
                <div>
                  <Dt value={today()} />
                </div>
              </div>
            </div>

            <h3 className="bill__section-title">پوری تفصیل</h3>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>تفصیل</th>
                    <th>وزن</th>
                    <th>ریٹ</th>
                    <th>سودا</th>
                    <th>وصولی</th>
                    <th>بقایا</th>
                  </tr>
                </thead>
                <tbody>
                  {/*
                    The two opening lines belong to a bill that starts at the
                    beginning, and only to that bill.

                    They carry what the customer owed when the old book was
                    copied in, which is the start of EVERYTHING. The بقایا column
                    beside them is a running total over the whole history. Put a
                    date on the bill and only the rows inside it are listed - so
                    پرانا بقایا 17,050 ends up printed directly above a بقایا of
                    67,650 with one 200 sale between them, and the 50,400 that
                    explains the gap is sitting in the rows the date just hid.
                    Nobody reading that believes the rest of the page.

                    With no date on, every row is listed, the arithmetic runs
                    unbroken from these lines to the last, and they belong.
                  */}
                  {/*
                    What a dated bill opens with.

                    Without it the first row's running balance arrives out of
                    nowhere - 165,480 sitting beside a 200 rupee sale - and the
                    customer holding the sheet has no way to check it. With it he
                    can run a finger down: this is what was already against my
                    name, this is what I took, this is what I paid, this is what
                    is left.

                    Only on a dated bill. An undated one starts at the beginning
                    and prints the old book's figure below under its own name;
                    two opening lines saying nearly the same thing would be worse
                    than one.
                  */}
                  {data.filtered && (
                    <tr className="row-opening">
                      <td>—</td>
                      <td>پچھلا بقایا</td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">
                        <Amt value={data.balanceBefore} />
                      </td>
                    </tr>
                  )}

                  {!data.filtered && Number(data.totals.openingSold) > 0 && (
                    <tr className="row-opening">
                      <td>—</td>
                      <td>پرانا بقایا</td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">
                        <Amt value={data.totals.openingSold} />
                      </td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                    </tr>
                  )}

                  {!data.filtered && Number(data.totals.openingPaid) > 0 && (
                    <tr className="row-opening">
                      <td>—</td>
                      <td>پرانی ادائیگی</td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">
                        <Amt value={data.totals.openingPaid} />
                      </td>
                      <td className="num">—</td>
                    </tr>
                  )}

                  {data.entries.map((entry) => (
                    <tr key={entry.id} className={entry.kind === 'PAYMENT' ? 'row-payment' : undefined}>
                      <td>
                        <Dt value={entry.date} />
                      </td>
                      {/* For a payment the description carries whatever note was
                          typed at the counter - "بذریعہ ایزی پیسہ", a cheque
                          number - and that is exactly what settles a query later,
                          so it belongs on the bill rather than being dropped. */}
                      <td>
                        {entry.kind === 'PAYMENT'
                          ? entry.description
                            ? `ادائیگی — ${entry.description}`
                            : 'ادائیگی'
                          : entry.description}
                      </td>
                      <td className="num">{entry.weight ? <Wt value={entry.weight} /> : '—'}</td>
                      <td className="num">{entry.rate ? <Amt value={entry.rate} /> : '—'}</td>
                      <td className="num">
                        {entry.kind === 'SALE' ? <Amt value={entry.debit} /> : '—'}
                      </td>
                      <td className="num">
                        {entry.kind === 'PAYMENT' ? (
                          <strong>
                            <Amt value={entry.credit} />
                          </strong>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="num">
                        <Amt value={entry.runningBalance} tone="muted" />
                      </td>
                    </tr>
                  ))}

                  {data.entries.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <Empty>اِن دنوں میں کچھ نہیں لکھا گیا۔</Empty>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/*
              One figure, and it is the only one the customer came for: what he
              owes. What he has bought and paid across every year he has dealt
              with the shop is the owner's business, not a line on a receipt, so
              those two now sit above this bill where only Uzair sees them.

              And it answers the question the heading asks. A bill headed "1 to
              15 September" closing with a balance from October is two answers on
              one page, and the customer has no way to tell which one he is being
              asked for.
            */}
            {/*
              The same headline the grower's bill ends on, because it is the
              same kind of sentence: one figure, large, and the only thing on
              the page the person holding it came to find out.

              Red, and red here only. The grower's bill is never red - owing a
              grower is ordinary trade, and colouring it every day would teach
              the eye to ignore red on this side, where it means somebody has
              not paid.
            */}
            <div className="final-line">
              <span className="final-line__label">
                {owedAtClose < 0 ? 'آپ کو واپس کرنے ہیں' : 'بقایا'}
                {data.filtered && (
                  <span className="muted small"> — {formatDate(to || '')} تک</span>
                )}
              </span>
              <span className={`final-line__value ${owedAtClose > 0 ? 'amt--debt' : ''}`}>
                {formatMoney(String(Math.abs(owedAtClose)))}
              </span>
            </div>

            <BillFooter />
          </div>
        </div>
      )}

      {/* Outside the bill div on purpose: never part of the printed or shared bill. */}
      {data && (
        <div className="no-print">
          <Card
            title="انٹری بدلیں یا ڈیلیٹ کریں"
            actions={<span className="muted small">یہ لسٹ بل میں نہیں جاتی</span>}
          >
            <p className="muted small" style={{ marginTop: 0 }}>
              بھاؤ تاؤ کے بعد ریٹ یا وزن بدل گیا، یا وصولی کی رقم غلط لکھی گئی؟ یہیں ٹھیک کر لیں — بل
              خود بخود نیا بن جائے گا۔
            </p>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>قسم</th>
                    <th>تفصیل</th>
                    <th>وزن</th>
                    <th>ریٹ</th>
                    <th>رقم</th>
                    <th className="col-actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((entry) => (
                    <tr key={entry.id} className={entry.kind === 'PAYMENT' ? 'row-payment' : undefined}>
                      <td>
                        <Dt value={entry.date} />
                      </td>
                      <td>{entry.kind === 'PAYMENT' ? 'ادائیگی' : 'سودا'}</td>
                      <td>{entry.kind === 'PAYMENT' ? entry.description || 'وصولی' : entry.description}</td>

                      {/* A payment has no weight or rate - one figure to correct, and
                          it lives in the رقم column. */}
                      <td className="num">
                        {entry.kind === 'SALE' && entry.weight && entry.rate ? (
                          <NumberCell
                            ariaLabel="وزن"
                            value={entry.weight}
                            decimals
                            width={88}
                            onCommit={(value) => void repriceSale(entry.id, value, entry.rate!)}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="num">
                        {entry.kind === 'SALE' && entry.weight && entry.rate ? (
                          <NumberCell
                            ariaLabel="ریٹ"
                            value={entry.rate}
                            width={88}
                            onCommit={(value) => void repriceSale(entry.id, entry.weight!, value)}
                          />
                        ) : (
                          '—'
                        )}
                      </td>
                      {/* A sale is corrected through its weight and rate, which
                          recompute the amount. A payment has no such parts, so
                          the amount itself is the editable figure. */}
                      <td className="num">
                        {entry.kind === 'PAYMENT' ? (
                          <NumberCell
                            ariaLabel="رقم"
                            value={entry.credit}
                            width={110}
                            onCommit={(value) =>
                              void correctPayment(entry.id, entry.date, entry.description, value)
                            }
                          />
                        ) : (
                          <strong>
                            <Amt value={entry.debit} />
                          </strong>
                        )}
                      </td>
                      <td>
                        <ConfirmDelete onConfirm={() => void removeEntry(entry.kind, entry.id)} />
                      </td>
                    </tr>
                  ))}

                  {data.entries.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <Empty>بدلنے کے لیے کوئی انٹری نہیں۔</Empty>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="muted small" style={{ marginTop: 10 }}>
              خانے میں نئی رقم لکھ کر <strong>Enter</strong> دبائیں یا باہر ٹچ کریں — تبدیلی خود محفوظ
              ہو جاتی ہے۔ ڈیلیٹ کی ہوئی انٹری «مزید» میں سے 30 دن تک واپس آ سکتی ہے۔
            </p>
          </Card>
        </div>
      )}
    </>
  )
}
